import React, { useState, useEffect,useRef } from 'react';
import { FaTemperatureHigh, FaTint, FaDesktop } from "react-icons/fa";
import { useNavigate } from 'react-router-dom';
import useZoneData from './useZonedata';
import { Hand, ArrowUpDown, Laptop, Briefcase, Lightbulb } from 'lucide-react';
import WeatherAirQuality from './WeatherAirQuality';
import { useSearchParams } from "react-router-dom";
import { set } from 'react-datepicker/dist/date_utils';

import {
  FloorLevel,
  getFacilityFloor,
  mapCategoryToFacility,
  SPECIAL_FACILITY_URLS,
} from "./facilityUtils";


interface SubItem { name: string; query_id?: string; }

    interface SliderItem {
    id: string;
    text: string;
    created_at: string;
    image: string;
    qrimage?: string | null; 
    }

    interface Subcategory { name: string; query_id?: string; subItems?: SubItem[]; }


    interface Category { name: string; query_id?: string; subcategories: Subcategory[]; }


    interface APISubItem {
    name: string;
    id: string;
    query_id?: string;
    description?: string;
    image?: string;
    section_title_l3?: string; 
    }

    interface APISubcategory { name: string; id: string; query_id?: string; section_title_l2?: string; subItems?: APISubItem[]; }

    interface APICategory { name: string; id: string; query_id?: string; subcategories: APISubcategory[]; }


const useFacilityData = () => {
  const [facilities, setFacilities] = useState<APICategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        setLoading(true);
        
        // Fetch first levels only
        const response = await fetch(
          "https://facility-search-demo-dot-optimus-lnu.df.r.appspot.com/updated/api/library_facilities/"  // Note the trailing slash
        );
        const data = await response.json();
        
        // Now need to fetch children for each first level to get complete data
        const firstLevels = data.data || [];
        
        // Fetch all children in parallel
        const facilitiesWithChildren = await Promise.all(
          firstLevels.map(async (firstLevel: any) => {
            try {
              const childrenResponse = await fetch(
                `https://facility-search-demo-dot-optimus-lnu.df.r.appspot.com/updated/api/library_facilities/children/${firstLevel.id}`
              );
              const childrenData = await childrenResponse.json();
              
              return {
                ...firstLevel,
                second_levels: childrenData.second_levels || []
              };
            } catch (err) {
              console.error(`Failed to fetch children for ${firstLevel.id}:`, err);
              return {
                ...firstLevel,
                second_levels: []
              };
            }
          })
        );
        
        const transformedData = transformAPIData(facilitiesWithChildren);
        setFacilities(transformedData);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to fetch facilities"
        );
        setFacilities([]);
      } finally {
        setLoading(false);
      }
    };
    fetchFacilities();
  }, []);

  return { facilities, loading, error };
};

const useSliderData = () => {
  const [data, setData] = useState<SliderItem[]>([]);
  const [duration, setDuration] = useState<number>(5); // Add this line
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchSliderData = async () => {
      try {
        setLoading(true);
        const response = await fetch("https://facility-search-demo-dot-optimus-lnu.df.r.appspot.com/updated/api/library_facilities/text_images");
        const result = await response.json();
        setData(result.data || []);
        setDuration(result.second || 5); // Add this line - get duration from API
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch slider data");
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchSliderData();
  }, []);

  return { data, duration, loading, error }; // Add duration to return
};

const transformAPIData = (apiData: any[]): APICategory[] => {
  return apiData
    .filter((item) => !item.deleted)
    .map((firstLevel) => ({
      name: firstLevel.name,
      query_id: firstLevel.query_id, 
      id: firstLevel.id,
      subcategories:
        firstLevel.second_levels
          ?.filter((item: any) => !item.deleted)
          ?.map((secondLevel: any) => ({
            name: secondLevel.name,
            id: secondLevel.id,
            query_id: secondLevel.query_id, 
            section_title_l2: secondLevel.section_title_l2,
            subItems:
              secondLevel.third_levels
                ?.filter((item: any) => !item.deleted)
                ?.map((thirdLevel: any) => ({
                  name: thirdLevel.name,
                  id: thirdLevel.id,
                  query_id: thirdLevel.query_id,
                  description: thirdLevel.description,
                  image: thirdLevel.image,
                  section_title_l3: thirdLevel.section_title_l3,
                })) || [],
          })) || [],
    }));
};

type OccupancyStatus = 'available' | 'lessAvailable' | 'crowded';

const getKioskConfig = () => {
  const hostname = window.location.hostname;
  
  const kioskConfigs: Record<string, { timeout: number | null; homeFloor: string | null; homePath: string }> = {
    'facility-search.library.ln.edu.hk': { timeout: null, homeFloor: null, homePath: '/' },
    '1f-kiosk.library.ln.edu.hk':        { timeout: 60000, homeFloor: '1/F', homePath: '/first-floor' },
    '2f-kiosk.library.ln.edu.hk':        { timeout: 60000, homeFloor: '2/F', homePath: '/second-floor' },
    '3f-kiosk.library.ln.edu.hk':        { timeout: 60000, homeFloor: '3/F', homePath: '/third-floor' },
    'mf-kiosk.library.ln.edu.hk':        { timeout: 60000, homeFloor: 'M/F', homePath: '/m-floor' },
    'lrr-kiosk.library.ln.edu.hk':       { timeout: 60000, homeFloor: '1/F', homePath: '/lrr' },
    'mfss-kiosk.library.ln.edu.hk':      { timeout: 60000, homeFloor: 'M/F', homePath: '/mfc' },
  };
  
  return kioskConfigs[hostname] || { timeout: null, homeFloor: null, homePath: '/' };
};



const MFZoomViewer: React.FC = () => {
  const navigate = useNavigate();
  const [redirectTimer, setRedirectTimer] = useState<NodeJS.Timeout | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
    const [specialMapUrl, setSpecialMapUrl] = useState<string | null>(null);
  
  const selectedLevel: FloorLevel = "M/F"; // Always M/F for this component
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [expandedSubcategories, setExpandedSubcategories] = useState<Record<string, boolean>>({});
  const [facility, setFacility] = useState<string>("");
  const [isDisplayToggled, setIsDisplayToggled] = useState<boolean>(() => {
    const displayParam = new URLSearchParams(window.location.search).get("display");
    return displayParam === "toggled";
  });    
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
      const [currentSlide, setCurrentSlide] = useState<number>(0);
      const [sliderLoading, setSliderLoading] = useState(true);
    
      const { data: sliderApiData, duration: sliderApiDuration, loading: sliderApiLoading } = useSliderData();
  const [openingHours, setOpeningHours] = useState<string>("Loading...");
    const {
      facilities: apiFacilities,
      loading: facilitiesLoading,
      error: facilitiesError,
    } = useFacilityData();
    const [isPanelCollapsed, setIsPanelCollapsed] = useState<boolean>(false);
const [queryId, setQueryId] = useState<string>("");
const [searchParams] = useSearchParams();

useEffect(() => {
  if (sliderApiData.length > 0) {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % sliderApiData.length);
    }, sliderApiDuration * 1000); // Use API duration in milliseconds

    return () => clearInterval(interval);
  }
}, [sliderApiData.length, sliderApiDuration]); // Add sliderApiDuration to dependencies

useEffect(() => {
  if (specialMapUrl) {
    const timer = setTimeout(() => {
      setSpecialMapUrl(null);
      setFacility("");
      setSearchTerm("");
      setExpandedCategories({});
      setExpandedSubcategories({});
    }, 60000); 

    return () => clearTimeout(timer);
  }
}, [specialMapUrl]);
  
  // Use real data hook
const { zoneData, floorAverages, crData, mprData, cpr2Data, loading, error } = useZoneData();




const handleSpecialFacilities = (facilityName: string): boolean => {
  const url = SPECIAL_FACILITY_URLS[facilityName];
  if (url) {
    setSpecialMapUrl(url);
    setFacility(mapCategoryToFacility(facilityName));
    setTimeout(() => {
      const iframe = document.getElementById('iframe') as HTMLIFrameElement;
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage({
          type: 'SHOW_TOOLTIP',
          facilityName: mapCategoryToFacility(facilityName)
        }, '*');
      }
    }, 3000);
    return true;
  }
  return false;
};


const fetchOpeningHours = async () => {
  try {
    const response = await fetch('https://lnudevices-dot-optimus-hk.df.r.appspot.com/library-opening-hours', {
      mode: 'cors'
    });
    const html = await response.text();
    
    // Create a temporary DOM element to parse the HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Find the opening hours content
    const contentDiv = doc.querySelector('.floor-plan-opening-hour .content');
    if (contentDiv) {
      let hours = contentDiv.textContent?.trim() || "Hours not available";
      // Clean up any HTML entities like &nbsp;
      hours = hours.replace(/\u00A0/g, ' '); // Replace non-breaking spaces
      setOpeningHours(hours);
    } else {
      setOpeningHours("Hours not available");
    }
  } catch (error) {
    console.error('Error fetching opening hours:', error);
    setOpeningHours("8:30 a.m. - 9:00 p.m. (Overnight Study Area Open)"); // Fallback
  }
};

  // SVG icons
  const availableIcon = (
    <svg
      width="48"
      height="32"
      viewBox="0 0 70 40"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g>
        <circle cx="15" cy="10" r="7" fill="#4ade80" /> 
        <path d="M7,20 Q15,13 23,20 L23,32 Q15,36 7,32 Z" fill="#4ade80" />
        <circle
          cx="35"
          cy="8"
          r="7"
          stroke="#4ade80"
          strokeWidth="1.5"
          fill="none"
        />
        <path
          d="M27,18 Q35,11 43,18 L43,30 Q35,34 27,30 Z"
          stroke="#4ade80"
          strokeWidth="1.5"
          fill="none"
        />
        <circle
          cx="55"
          cy="10"
          r="7"
          stroke="#4ade80"
          strokeWidth="1.5"
          fill="none"
        />
        <path
          d="M47,20 Q55,13 63,20 L63,32 Q55,36 47,32 Z"
          stroke="#4ade80"
          strokeWidth="1.5"
          fill="none"
        />
      </g>
    </svg>
  );
 
  const lessAvailableIcon = (
    <svg
      width="48"
      height="32"
      viewBox="0 0 70 40"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g>
        <circle cx="15" cy="10" r="7" fill="#fbbf24" /> 
        <path d="M7,20 Q15,13 23,20 L23,32 Q15,36 7,32 Z" fill="#fbbf24" />
        <circle cx="35" cy="8" r="7" fill="#fbbf24" /> 
        <path
          d="M27,18 Q35,11 43,18 L43,30 Q35,34 27,30 Z"
          fill="#fbbf24"
        />
        <circle
          cx="55"
          cy="10"
          r="7"
          stroke="#fbbf24"
          strokeWidth="1.5"
          fill="none"
        />
        <path
          d="M47,20 Q55,13 63,20 L63,32 Q55,36 47,32 Z"
          stroke="#fbbf24"
          strokeWidth="1.5"
          fill="none"
        />
      </g>
    </svg>
  );
 
  const crowdedIcon = (
    <svg
      width="48"
      height="32"
      viewBox="0 0 70 40"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g>
        <circle cx="15" cy="10" r="7" fill="#ef4444" />
        <path d="M7,20 Q15,13 23,20 L23,32 Q15,36 7,32 Z" fill="#ef4444" />
        <circle cx="35" cy="8" r="7" fill="#ef4444" /> 
        <path
          d="M27,18 Q35,11 43,18 L43,30 Q35,34 27,30 Z"
          fill="#ef4444"
        />
        <circle cx="55" cy="10" r="7" fill="#ef4444" /> 
        <path
          d="M47,20 Q55,13 63,20 L63,32 Q55,36 47,32 Z"
          fill="#ef4444"
        />
      </g>
    </svg>
  );

  // Floor map URL for M/F only
  const mezzanineFloorMapUrl = "https://pwwpdev.github.io/Lingnan/mfc_facility.html";

  // Navigation function for different floors
  const navigateToFloor = (level: FloorLevel): void => {
    const params = new URLSearchParams();
  if (isDisplayToggled) params.append('display', 'toggled');
  
  const queryString = params.toString();
  const suffix = queryString ? `?${queryString}` : '';
    switch (level) {
        case "1/F": navigate(`/first-floor${suffix}`); break;
      case "M/F":
        // Already on M/F, do nothing
        break;
        case "2/F": navigate(`/second-floor${suffix}`); break;
        case "3/F": navigate(`/third-floor${suffix}`); break;
      default:
        break;
    }
  };

  // categories and subcategories for the multilevel dropdown
  const [categories, setCategories] = useState([
    {
      "name": "Service Counter (1/F Zone A)",
      "subcategories": []
    },
    {
      "name": "Collections",
      "subcategories": [
        {
          "name": "Books A-Z",
          "subItems": [
            { "name": "Books A-DS776 (1/F Zone A)" },
            { "name": "Books DS777-HJ (1/F Zone C)" },
            { "name": "Books HM (M/F Zone B)" },
            { "name": "Books HM1101-HN (2/F Zone B)" },
            { "name": "Books HN-HS (3/F Zone A)" },
            { "name": "Books HT-NX (3/F Zone B)" },
            { "name": "Books P-ZA (3/F Zone C)" }
          ]
        },
        {
          "name": "Course Reserves (1/F Zone A)",
          "subItems": [
            { "name": "Good Reads, New Arrivals (1/F Zone B)" },
            { "name": "High Use Books (1/F Zone B)" }
          ]
        },
        {
          "name": "Journals [SER] (2/F Zone A)",
          "subItems": [
            { "name": "Current Journals (2/F Zone A)" },
            { "name": "Bound Journals (2/F Zone A)" }
          ]
        },
        { "name": "Language Examinations & Learning Resources [LANG] (2/F Zone C)" },
        { "name": "Large Books [LBK], Extra Large Books [XLBK] (3/F Zone A)" },
        { "name": "Lingnan University Archives (2/F Zone A)" },
        {
          "name": "Multimedia [AVM]",
          "subItems": [
            { "name": "Multimedia (2/F Zone C)" },
            { "name": "Category III Multimedia - Ask at Service Counter (1/F Zone A)" }
          ]
        },
        {
          "name": "Newspapers",
          "subItems": [
            { "name": "Current Issues (2/F Zone B)" },
            { "name": "Back Issues (2/F Zone A)" }
          ]
        },
        {
          "name": "References [REF]",
          "subItems": [
            { "name": "References A-HM (2/F Zone A)" },
            { "name": "References HN-Z (2/F Zone C)" }
          ]
        },
        { "name": "Staff Publications (1/F Zone B)" }
      ]
    },
    {
      "name": "Computers",
      "subcategories": [
        { "name": "Chiang Chen Information Commons (1/F Zone A)" },
        { "name": "Computer Rooms (1/F Zone B)" },
        { "name": "Lee Hak Kan Multimedia and Language Learning Centre - MLLC (2/F Zone C)" },
        {
          "name": "SEN Workstations",
          "subItems": [
            { "name": "SEN Workstation 1 (1/F Zone B)" },
            { "name": "SEN Workstation 2 (1/F Zone C)" }
          ]
        },
        { "name": "Bloomberg Terminal - Database Workstation (1/F Zone B)" },
        { "name": "LSEG Workspace (1/F Zone B)" },
        { "name": "WIND Terminal (1/F Zone B)" }
      ]
    },
    {
      "name": "MultiFunction Printers",
      "subcategories": [
        { "name": "Copiers/Printers/Scanners (Section Title - for display only)" },
        { "name": "11-13 (1/F Zone A)" },
        { "name": "14-16BW (1/F Zone B)" },
        { "name": "17 (1/F Zone C)" },
        { "name": "21, 23 (2/F Zone C)" },
        { "name": "22BW (2/F Zone A)" },
        { "name": "31BW-32 (3/F Zone B)" }
      ]
    },
    {
      "name": "Study Rooms",
      "subcategories": [
        { "name": "Individual Study Rooms (Section Title - for display only)" },
        { "name": "Individual Study Rooms 1 - 7 (1/F Zone C)" },
        { "name": "Individual Study Rooms 8 (1/F Zone C)" },
        { "name": "Individual Study Rooms 9 - 11 (1/F Zone A)" },
        { "name": "Group Study Rooms (Section Title - for display only)" },
        { "name": "Group Study Rooms 1 - 4 (1/F Zone C)" },
        { "name": "Group Study Room 5 (1/F Zone C)" },
        { "name": "Group Study Rooms 8 - 9 (1/F Zone A)" },
        { "name": "Group Study Room 10 - 11 (2/F Zone B)" }
      ]
    },
    {
      "name": "Quiet Study Spaces",
      "subcategories": [
        { "name": "1/F Study Area (Section Title - for display only)" },
        { "name": "Late Reading Room (1/F Zone B)" },
        { "name": "Postgraduate Research Carrels (1/F Zone B)" },
        { "name": "Individual Study Carrels (1/F Zone A & Zone C)" },
        { "name": "M/F Study Area (Section Title - for display only)" },
        { "name": "Individual Study Carrels (M/F Zone C)" },
        { "name": "Postgraduate Lounge (M/F Zone C)" },
        { "name": "2/F Study Area (Section Title - for display only)" },
        { "name": "Chang Han-tsiu Reading Room (2/F Zone A)" },
        { "name": "3/F Study Area (Section Title - for display only)" },
        { "name": "Multi-Purpose Room 1 (3/F Zone C)" },
        { "name": "Multi-Purpose Room 2 (3/F Zone A)" }
      ]
    },
    {
      "name": "Group Study Spaces",
      "subcategories": [
        { "name": "1/F Study Area (Section Title - for display only)" },
        { "name": "Activity Room (1/F Zone A)" },
        { "name": "Café (1/F Zone A)" },
        { "name": "Computer Room 1 & 2 (1/F Zone B)" },
        { "name": "M/F Study Area (Section Title - for display only)" },
        { "name": "LU Barn (Makerspace) (M/F Zone A)" },
        { "name": "2/F Study Area (Section Title - for display only)" },
        { "name": "Lee Hak Kan Multimedia and Language Learning Centre - MLLC (2/F Zone C)" },
        { "name": "Mini Theatre (2/F Zone B)" }
      ]
    },
    {
      "name": "Overnight Study Area",
      "subcategories": [
        { "name": "Late Reading Room (1/F Zone B)" },
        { "name": "Computer Room 1 & 2 (1/F Zone B)" }
      ]
    },
    {
      "name": "Drinking Water Stations",
      "subcategories": [
        { "name": "1/F (1/F Zone B)" },
        { "name": "2/F (2/F Zone A)" },
        { "name": "3/F (3/F Zone A & Zone C)" }
      ]
    },
    {
      "name": "Toilets",
      "subcategories": [
        { "name": "1/F Toilets (Section Title - for display only)" },
        { "name": "1/F Male & Female (Zone A & Zone C)" },
        { "name": "Unisex - Available during Overnight Study Period (Zone B)" },
        { "name": "1/F Accessible Toilets (Zone A & Zone C)" },
        { "name": "2/F Toilets (Section Title - for display only)" },
        { "name": "2/F Male & Female (Zone A & Zone C)" },
        { "name": "3/F Toilets (Section Title - for display only)" },
        { "name": "3/F Male & Female (Zone A & Zone C)" },
        { "name": "3/F Accessible Toilets (Zone C)" }
      ]
    },
    {
      "name": "Other Facilities",
      "subcategories": [
        { "name": "Book Drops (1/F Zone B)" },
        { "name": "Book Sterilizer (1/F Zone B)" },
        { "name": "Self-Checkout Machine (1/F Zone B)" },
        { "name": "Self-Pickup Locker (1/F Zone B)" },
        { "name": "SEN Equipment - Service Counter (1/F Zone B)" },
        {
          "name": "LU Barn (Makerspace) Equipment",
          "subItems": [
            { "name": "3D Printers - LU Barn (Makerspace) (M/F Zone A)" },
            { "name": "3D Scanner - LU Barn (Makerspace) (M/F Zone A)" },
            { "name": "Laser Cutter / Engraver - LU Barn (Makerspace) Maker Room (1/F Zone A)" }
          ]
        },
        { "name": "Lift (1/F - 3/F Zone B)" }
      ]
    }
  ]);
  

  const [filteredCategories, setFilteredCategories] = useState<Category[]>(categories);

  useEffect(() => {
    return () => {
      if (redirectTimer) clearTimeout(redirectTimer);
    };
  }, [redirectTimer]);

  useEffect(() => {
    const facilityParam = searchParams.get("facility") ? decodeURIComponent(searchParams.get("facility")!) : null;
    const queryParam = searchParams.get("query");
    const fromParam = searchParams.get("from");
    const displayParam = searchParams.get("display");
  
    if (facilityParam) {
      setFacility(facilityParam);
      if (queryParam) setQueryId(queryParam);
  
      if (fromParam && window.location.pathname !== fromParam) {
        const timer = setTimeout(() => {
          navigate(fromParam);
        }, 60000);
        setRedirectTimer(timer);
      }
    }
  
    if (displayParam === "toggled") setIsDisplayToggled(true);
  }, [searchParams, navigate]);

  // filter categories based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredCategories(categories);
      return;
    }
  
    const filtered = categories.filter((category: Category) => {
      // check if category name matches
      if ((category.name || "").toLowerCase().includes(searchTerm.toLowerCase())) {
        return true;
      }
  
      // check if any subcategory matches
      if (category.subcategories && category.subcategories.length > 0) {
        const hasMatchingSubcategory = category.subcategories.some(
          (subcategory: Subcategory) => {
            if (
              (subcategory.name || "").toLowerCase().includes(searchTerm.toLowerCase())
            ) {
              return true;
            }
  
            // check for sub-items matches
            if (subcategory.subItems && subcategory.subItems.length > 0) {
              return subcategory.subItems.some((subItem: SubItem) =>
                (subItem.name || "").toLowerCase().includes(searchTerm.toLowerCase())
              );
            }
  
            return false;
          }
        );
        return hasMatchingSubcategory;
      }
  
      return false;
    });
  
    setFilteredCategories(filtered);
  
    // Auto-expand matching categories and subcategories
    if (searchTerm) {
      const newExpandedCategories: Record<string, boolean> = {};
      const newExpandedSubcategories: Record<string, boolean> = {};
  
      filtered.forEach((category) => {
        // Expand category if it has subcategories and contains matches
        if (category.subcategories && category.subcategories.length > 0) {
          newExpandedCategories[category.name] = true;
  
          // Check subcategories for matches
          category.subcategories.forEach((subcategory) => {
            // Expand subcategory if it has sub-items and contains matches
            if (subcategory.subItems && subcategory.subItems.length > 0) {
              const hasMatchingSubItem = subcategory.subItems.some((subItem) =>
                (subItem.name || "").toLowerCase().includes(searchTerm.toLowerCase())
              );
              if (hasMatchingSubItem || (subcategory.name || "").toLowerCase().includes(searchTerm.toLowerCase())) {
                newExpandedSubcategories[subcategory.name] = true;
              }
            }
          });
        }
      });
  
      setExpandedCategories(newExpandedCategories);
      setExpandedSubcategories(newExpandedSubcategories);
    }
  }, [searchTerm, categories]);

  useEffect(() => {
    if (apiFacilities && apiFacilities.length > 0) {
      const transformedCategories: Category[] = apiFacilities.map((apiCat) => ({
        name: apiCat.name,
        subcategories: apiCat.subcategories.map((apiSub) => ({
          name: apiSub.name,
          subItems:
            apiSub.subItems?.map((apiSubItem) => ({
              name: apiSubItem.name,
            })) || [],
        })),
      }));
  
      setCategories(transformedCategories);
      console.log("Updated categories from API:", transformedCategories);
    }
  }, [apiFacilities]);

  // Fetch opening hours on component mount and every hour
useEffect(() => {
  // Fetch immediately
  fetchOpeningHours();
  
  // Set up interval to fetch every hour (3600000 milliseconds)
  const hoursInterval = setInterval(fetchOpeningHours, 3600000);
  
  // Cleanup interval on component unmount
  return () => {
    if (hoursInterval) {
      clearInterval(hoursInterval);
    }
  };
}, []);

// AUTO-RELOAD-30MIN-START
// Auto-reload the entire page every 30 minutes to pick up fresh content/updates.
// This is independent of the kiosk idle-timeout redirect-to-home-floor logic
// defined elsewhere in this file, which is left untouched.
useEffect(() => {
  const AUTO_RELOAD_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes
  const autoReloadTimer = setInterval(() => {
    window.location.reload();
  }, AUTO_RELOAD_INTERVAL_MS);

  return () => {
    clearInterval(autoReloadTimer);
  };
}, []);
// AUTO-RELOAD-30MIN-END

// CORRECTED useEffect FOR KIOSK TIMEOUT
useEffect(() => {
  const config = getKioskConfig();
  
  // Only set up timeout if this kiosk has timeout configured
  if (config.timeout !== null && config.homeFloor !== null) {
    const homeFloor = config.homeFloor; // TypeScript now knows this is string
    const timeout = config.timeout; // TypeScript now knows this is number
    
    const resetTimer = () => {
      // Clear existing timer
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      // Start new timer
      timeoutRef.current = setTimeout(() => {
        setSearchTerm("");
        setFacility("");
        setExpandedCategories({});
        setExpandedSubcategories({});
        setSpecialMapUrl(null);
        setQueryId("");
        navigate(config.homePath);
            }, timeout);
    };
    
    // Set up event listeners to reset timer on user interaction
    const events = ['click', 'touchstart', 'keypress', 'mousemove'];
    events.forEach(event => {
      window.addEventListener(event, resetTimer);
    });
    
    // Start initial timer
    if (!facility) return;
    resetTimer();
    
    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      events.forEach(event => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }
}, [navigate, isDisplayToggled, facility]);

 // toggle expansion of category
const toggleCategory = (categoryName: string): void => {
  if (handleSpecialFacilities(categoryName)) {
    return; // Exit early if this was a special facility
  }
  // Set facility and reload map if category has no subcategories
  const category = categories.find((cat) => cat.name === categoryName);
  if (!category?.subcategories || category.subcategories.length === 0) {
    // Check if facility is on a different floor
    const facilityFloor = getFacilityFloor(mapCategoryToFacility(categoryName)); 
    
    if (facilityFloor && facilityFloor !== "M/F") {
      setSpecialMapUrl(null); 
      // Navigate to the correct floor with facility parameter
      const params = new URLSearchParams();
      params.append('facility', encodeURIComponent(mapCategoryToFacility(categoryName)));
      params.append('from', getKioskConfig().homePath);
      if (isDisplayToggled) params.append('display', 'toggled');
      
      let floorRoute = "";
      switch (facilityFloor) {
        case "1/F": floorRoute = "first-floor"; break;
        case "3/F": floorRoute = "third-floor"; break;
        case "2/F": floorRoute = "second-floor"; break;
        default:    floorRoute = "m-floor";
      }
      navigate(`/${floorRoute}?${params.toString()}`);
    } else {
      // Handle on current floor
      setSpecialMapUrl(null);
      const mappedFacility = mapCategoryToFacility(categoryName);
      setFacility(mappedFacility);
      console.log("Original category:", categoryName);
      console.log("Mapped facility:", mappedFacility);
    }
  }

  // Accordion behavior: close all other categories and toggle current one
  setExpandedCategories((prev) => {
    const isCurrentlyExpanded = prev[categoryName];

    // Create new state with all categories closed
    const newState: Record<string, boolean> = {};
    Object.keys(prev).forEach((key) => {
      newState[key] = false;
    });

    // If the current category was closed, open it; if it was open, keep it closed
    newState[categoryName] = !isCurrentlyExpanded;

    return newState;
  });

  // Also close all subcategories when switching between main categories
  if (category?.subcategories && category.subcategories.length > 0) {
    setExpandedSubcategories({});
  }
};
  
  // toggle expansion of subcategory
  const toggleSubcategory = (subcategoryName: string): void => {
    // Accordion behavior: close all other subcategories and toggle current one
    setExpandedSubcategories((prev) => {
      const isCurrentlyExpanded = prev[subcategoryName];
  
      // Create new state with all subcategories closed
      const newState: Record<string, boolean> = {};
      Object.keys(prev).forEach((key) => {
        newState[key] = false;
      });
  
      // If the current subcategory was closed, open it; if it was open, keep it closed
      newState[subcategoryName] = !isCurrentlyExpanded;
  
      return newState;
    });
  };

  // helper function to render occupancy icon based on status
  const renderOccupancyIcon = (status: OccupancyStatus): JSX.Element => {
    switch (status) {
      case "available":
        return availableIcon;
      case "lessAvailable":
        return lessAvailableIcon;
      case "crowded":
        return crowdedIcon;
      default:
        return availableIcon;
    }
  };

  // helper function to render CO2 level icon based on ppm
  const renderCO2StatusIcon = (ppm: number): JSX.Element => {
    if (ppm < 800) {
      return (
        <svg width="38" height="38" viewBox="0 0 24 24" fill="#4ade80" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="#4ade80" strokeWidth="2" fill="none" />
          <path d="M7,13 C9,17 15,17 17,13" stroke="#4ade80" strokeWidth="2" strokeLinecap="round" fill="none" />
          <circle cx="8" cy="9" r="1.5" fill="#4ade80" />
          <circle cx="16" cy="9" r="1.5" fill="#4ade80" />
        </svg>
      );
    } else if (ppm < 1500) {
      return (
        <svg width="38" height="38" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="#fbbf24" strokeWidth="2" fill="none" />
          <path d="M8,14 L16,14" stroke="#fbbf24" strokeWidth="2" />
          <circle cx="8" cy="9" r="1" fill="#fbbf24" />
          <circle cx="16" cy="9" r="1" fill="#fbbf24" />
        </svg>
      );
    } else {
      return (
        <svg width="38" height="38" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2" fill="none"  />
          <path d="M8,16 C8,14 16,14 16,16" stroke="#ef4444" strokeWidth="2" fill="none" />
          <circle cx="8" cy="9" r="1" fill="#ef4444" />
          <circle cx="16" cy="9" r="1" fill="#ef4444" />
        </svg>
      );
    }
  };

  

  // helper function to determine overall floor occupancy
  const getFloorOccupancy = (floor: string): OccupancyStatus => {
    if (!zoneData[floor]) return "available";
    const zones = Object.keys(zoneData[floor]);
    if (zones.length === 0) return "available";
  
    const statuses = zones.map(zone => zoneData[floor][zone].occupancy);
    
    if (statuses.includes("crowded")) return "crowded";
    if (statuses.includes("lessAvailable")) return "lessAvailable";
    return "available";
  };

  // helper function to render floor occupancy icon
  const renderFloorOccupancyIcon = (floor: string): JSX.Element => {
    const occupancy = getFloorOccupancy(floor);
    return renderOccupancyIcon(occupancy);
  };

  const getCurrentDate = (): string => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const now = new Date();
    const day = now.getDate();
    const month = months[now.getMonth()];
    const year = now.getFullYear();
    const weekday = days[now.getDay()];
    
    return `${day} ${month} ${year} ${weekday}`;
  };

  // Helper function to calculate floor averages and overall occupancy
const getFloorAverages = (floor: string) => {
    const zones = Object.keys(zoneData[floor] || {});
    if (zones.length === 0) return null;
    
    let totalTemp = 0, totalHumidity = 0, totalCO2 = 0;
    const occupancyCount = { available: 0, lessAvailable: 0, crowded: 0 };
    
    zones.forEach(zone => {
      const data = zoneData[floor][zone];
      totalTemp += data.temp;
      totalHumidity += data.humidity;
      totalCO2 += data.co2;
      occupancyCount[data.occupancy]++;
    });
    
    // Determine overall occupancy (prioritize crowded > lessAvailable > available)
    let overallOccupancy: OccupancyStatus = "available";
    if (occupancyCount.crowded > 0) overallOccupancy = "crowded";
    else if (occupancyCount.lessAvailable > 0) overallOccupancy = "lessAvailable";
    
    return {
      avgTemp: Math.round(totalTemp / zones.length),
      avgHumidity: Math.round(totalHumidity / zones.length),
      avgCO2: Math.round(totalCO2 / zones.length),
      occupancy: overallOccupancy
    };
  };
  return (
    <div className="flex flex-col h-screen">
  
      <div className="bg-gray-100 h-28 fixed top-0 left-0 w-full z-20 flex items-center justify-center px-4 border-b border-gray-200">
        <div className="flex items-center gap-4">
          <Hand className="w-10 h-10 text-gray-400" />
          <span className="text-gray-400 text-[22px] font-medium">
            輕觸屏幕以閱讀資訊 Touch to Navigate
          </span>
        </div>
      </div>
  
      {/* header */}
      <header className="bg-white custom-shadow mt-8 h-20 xl:h-[100px] lg:h-[140px] fixed top-20 left-0 w-full z-10 flex items-center justify-between px-4">        
        <div className="flex items-center h-full">
          <img 
            src="/library-logo-final_2024.png" 
            alt="Lingnan University Fong Sum Wood Library" 
            className="h-10 lg:h-[59px] xl:h-[58px] ml-1"
          />
        </div>
  
        <div className="flex items-center gap-4">
          <div className="text-gray-600 ">
            <div className="text-[22px] font-medium"> {getCurrentDate()}</div>
          </div>
          
          <WeatherAirQuality className="text-[16px] md:text-xl sm:text-lg lg:text-[22px]" />
        </div>
      </header>
      
      {/* banner */}
      <div className="bg-white p-3 flex justify-between items-center border-b mt-26 lg:mt-[250px] xl:mt-[220px]">
        <div className="text-gray-600 font-semibold text-sm lg:text-4xl">
          <span className="font-bold text-teal-600">
            Today's Opening Hours:
          </span>{" "}
          <span className="text-4xl">{openingHours}</span>
        </div>
      </div>
      
       {/* floor selector */}
<div className="flex justify-between items-center space-x-2 p-3 bg-white custom-shadow">
  <div className="flex space-x-3">
    <button
      onClick={() => navigateToFloor("1/F")}
      className={`rounded-full px-10 py-4 text-2xl transition-all duration-200 ${
        facility === "Late Reading Room (1/F Zone B)"
          ? "bg-[#440099] text-white font-semibold"
          : "bg-[#e5e7eb] text-[#545454] hover:bg-gray-300"
      }`}
    >
      1/F
    </button>
    <button
      onClick={() => navigateToFloor("M/F")}
      className={`rounded-full px-10 py-4 font-semibold text-2xl ${
        facility === "Late Reading Room (1/F Zone B)"
          ? "bg-[#e5e7eb] text-[#545454] hover:bg-gray-300"
          : "bg-[#5cb8b2] text-white"
      }`}
    >
      M/F
    </button>
    <button
      onClick={() => navigateToFloor("2/F")}
      className="rounded-full px-10 py-4  text-2xl transition-all duration-200 bg-[#e5e7eb] text-[#545454] hover:bg-gray-300"
    >
      2/F
    </button>
    <button
      onClick={() => navigateToFloor("3/F")}
      className="rounded-full px-10 py-4 text-2xl transition-all duration-200 bg-[#e5e7eb] text-[#545454] hover:bg-gray-300"
    >
      3/F
    </button>
  </div>
</div>
  
      <p className='px-6 py-4 text-2xl font-normal'>M/F | Individual Study Carrels</p>
  
      {/* CHANGED: Conditional rendering - Zone Info and Library Guidelines appear here when toggled */}
      {isDisplayToggled && (
        <>
          {/* Zone Info - New Averaged Display when toggled */}
          <div className="bg-white p-4 mt-4 ">
            
            {loading && (
              <div className="text-center py-4">
                <div className="text-gray-500">Loading zone data...</div>
              </div>
            )}
            
            {error && (
              <div className="text-center py-4">
                <div className="text-red-500">Error: {error}</div>
              </div>
            )}
            
            {!loading && !error && (() => {
              const floorData = getFloorAverages(selectedLevel);
              
              if (!floorData) {
                return (
                  <div className="text-center py-8">
                    <div className="text-gray-500">No zone data available for {selectedLevel}</div>
                  </div>
                );
              }
              
              return (
                <div className="relative bg-gradient-to-r from-cyan-100 to-cyan-100 mx-16 rounded-2xl p-6 overflow-hidden">
                  {/* Curved cutout background */}
                  <div className="absolute bottom-0 left-0 w-28 h-24 bg-white rounded-tr-full"></div>
                  
                  {/* People icon positioned in the cutout */}
                  <div className="absolute bottom-5 left-3 z-10 scale-[1.8]">
                    {renderOccupancyIcon(floorData.occupancy)}
                  </div>
                  
                  {/* Metrics - moved to give space for icon */}
                  <div className="flex items-center justify-center gap-32 ml-20">
                    {/* Temperature */}
                    <div className="text-center relative">
                      <FaTemperatureHigh className="text-amber-500 size-6 text-lg absolute -top-1 -left-7" />
                      <div className="text-4xl font-semibold text-teal-700 mb-1">{floorData.avgTemp}°C</div>
                      <div className="text-sm text-teal-600">Temperature</div>
                    </div>
                    
                    {/* Humidity */}
                    <div className="text-center relative">
                      <FaTint className="text-blue-400 size-7 text-lg absolute -top-1 -left-4" />
                      <div className="text-4xl font-semibold text-teal-700 mb-1">{floorData.avgHumidity}%</div>
                      <div className="text-sm text-teal-600">Relative Humidity</div>
                    </div>
                    
                    {/* CO2 */}
                    <div className="text-center relative">
                      <div className="scale-75 absolute -top-2 -left-6">
                        {renderCO2StatusIcon(floorData.avgCO2)}
                      </div>
                      <div className="text-4xl font-semibold text-teal-700 mb-1">
                        {floorData.avgCO2} <span className="text-sm text-gray-500">ppm</span>
                      </div>
                      <div className="text-sm text-teal-600">Carbon Dioxide (CO2)</div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
  
          {/* Library Guidelines Section when toggled */}
          <div className="bg-white p-2 mt-8">
            <div className="flex items-center gap-12 w-full justify-center mx-8">
              {/* Left side - Triangular icon formation */}
              <div className="flex-shrink-0 ml-3">
                <div className="relative w-24 h-20">
                  {/* Top icon */}
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center">
                    <Laptop className="w-7 h-7 text-blue-600" />
                  </div>
                  
                  {/* Bottom left icon */}
                  <div className="absolute -bottom-6 -left-4 w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
                    <Briefcase className="w-7 h-7 text-green-600" />
                  </div>
                  
                  {/* Bottom right icon */}
                  <div className="absolute -bottom-6 -right-4 w-14 h-14 bg-yellow-100 rounded-full flex items-center justify-center">
                    <Lightbulb className="w-7 h-7 text-yellow-600" />
                  </div>
                </div>
              </div>
  
              {/* Right side - Text content */}
              <div className="flex-1 space-y-4">
                {/* First message */}
                <div>
                  <p className="text-gray-800 text-[16px] leading-relaxed">
                    For unattended items found in the Library, please help relocate them to the nearby unattended items collection box.
                  </p>
                  <p className="text-gray-700 text-[18px] mt-1">
                    如發現圖書館內有無人看管的物品，請移至附近的閒置物品收集點。
                  </p>
                </div>
                
                {/* Second message */}
                <div>
                  <p className="text-gray-800 text-[16px] leading-relaxed">
                    Always be considerate and take your belongings with you ALL the time to vacate the study space.
                  </p>
                  <p className="text-gray-700 text-[18px] mt-1">
                    文明使用圖書館，共享學習空間。離開時，請緊記帶走個人物品。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      
      {/* main content - map and search facility */}
      <div className="flex">
        {/* 3d Viewer (75% width) - M/F Map Only */}
        <div className={`${isPanelCollapsed ? "w-full" : "w-3/4"} h-[40vh] relative`}>
 <iframe
  id="iframe"
  src={
    specialMapUrl ||
  `${mezzanineFloorMapUrl}${facility ? `?facility=${encodeURIComponent(facility)}` : ""}${queryId ? `${facility ? '&' : '?'}query=${queryId}` : ""}`
  }
  className="w-full h-full border-0"
  title="2/F Library Map"
/>
        </div>
        
        {/* right panel with search and categories (25% width) */}
        <div className={`${isPanelCollapsed ? "w-0 overflow-hidden" : "w-1/4"} h-[40vh] flex flex-col border-l bg-white border-gray-300 transition-all duration-300 ease-in-out relative`}>
          {/* search input */}
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                className="w-full px-8 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Search Location / Facility"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {facilitiesLoading && (
  <div className="p-2 bg-blue-50 border-b text-xs">
    <div className="text-blue-600">
      Loading...
    </div>
  </div>
)}

{facilitiesError && (
  <div className="p-2 bg-red-50 border-b text-xs">
    <div className="text-red-600">
      ⚠️ API Error: Using fallback data
    </div>
  </div>
)}

{!facilitiesLoading &&
  !facilitiesError &&
  apiFacilities.length > 0 && <div></div>}
              {searchTerm && (
                <button
                  className="absolute right-3 top-2 text-gray-400 hover:text-gray-600"
                  onClick={() => setSearchTerm("")}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
          
          {/* categories and subcategories(scrollable) */}
          <div className="overflow-y-auto flex-1">
            {filteredCategories.map((category, index) => (
              <div key={index} className="border-b-2 border-gray-200">
                <div
                  className="p-3 flex items-center justify-between cursor-pointer bg-teal-600 text-white"
                  onClick={() => toggleCategory(category.name)}
                >
                  <span className="font-medium text-sm">{category.name}</span>
                  {category.subcategories && category.subcategories.length > 0 && (
                    <svg
                      className={`w-4 h-4 transition-transform ${
                        expandedCategories[category.name] ? "transform rotate-90" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  )}
                </div>
                
                {/* subcategories */}
                {expandedCategories[category.name] &&
  category.subcategories && (
    <div className="bg-gray-50">
      {(() => {
        // Get the API category to access section_title_l2
        const apiCategory = apiFacilities.find(cat => cat.name === category.name);
        
        // Group subcategories by section_title_l2 with proper typing
        const groupedSubcategories = category.subcategories.reduce((groups: Record<string, typeof category.subcategories>, subcategory) => {
          const apiSub = apiCategory?.subcategories.find(sub => sub.name === subcategory.name);
          const sectionTitle = apiSub?.section_title_l2 || "no-section";
          
          if (!groups[sectionTitle]) {
            groups[sectionTitle] = [];
          }
          groups[sectionTitle].push(subcategory);
          return groups;
        }, {} as Record<string, typeof category.subcategories>);

        // Render groups
        return Object.entries(groupedSubcategories).map(([sectionTitle, subcategories], groupIndex) => (
          <div key={groupIndex}>
            {/* Section Title Header - only if sectionTitle exists and isn't null/empty/no-section */}
            {sectionTitle && sectionTitle.trim() && sectionTitle !== "null" && sectionTitle !== "no-section" && (
              <div className="bg-gray-200 cursor-default p-2 pl-6 border-t border-gray-200">
                <div className="font-bold text-sm text-gray-800">
                  {sectionTitle}
                </div>
              </div>
            )}
            
            {/* Subcategories under this section */}
            {subcategories.map((subcategory, subIndex) => (
              <div key={subIndex}>
                <div
                  className="p-2 pl-6 border-t border-gray-200 bg-white hover:bg-gray-100 flex items-center justify-between cursor-pointer"
                  onClick={() => {
                    if (subcategory.subItems && subcategory.subItems.length > 0) {
                      toggleSubcategory(subcategory.name);
                    } else {
                      if (handleSpecialFacilities(subcategory.name)) {
                        return;
                      }
                      const apiCategory = apiFacilities.find(cat => cat.name === category.name);
                      const apiSub = apiCategory?.subcategories.find(sub => sub.name === subcategory.name);
                      
                      const queryIdValue = apiSub?.query_id || subcategory.name;
                      const facilityFloor = getFacilityFloor(queryIdValue) || getFacilityFloor(mapCategoryToFacility(subcategory.name));
                  
                      if (facilityFloor && facilityFloor !== "M/F") {
                        setSpecialMapUrl(null); 
                        const params = new URLSearchParams();
                        params.append('facility', encodeURIComponent(queryIdValue));
                        params.append('from', getKioskConfig().homePath);
                        if (isDisplayToggled) params.append('display', 'toggled');
                        
                        let floorRoute = "";
                        switch (facilityFloor) {
                            case "1/F": floorRoute = "first-floor"; break;
                            case "3/F": floorRoute = "third-floor"; break;
                            case "2/F": floorRoute = "second-floor"; break;
                            default:    floorRoute = "m-floor";
                          }
                          navigate(`/${floorRoute}?${params.toString()}`);
                      } else {
                        setSpecialMapUrl(null);
                        setFacility(queryIdValue);
                        setQueryId(queryIdValue);
                        console.log("API Facility selected:", subcategory.name);
                        console.log("Query ID:", queryIdValue);
                      }
                    }
                  }}
                >
                  <div>
                    <div className="font-medium text-xs">
                      {subcategory.name}
                    </div>
                  </div>
                  {subcategory.subItems && subcategory.subItems.length > 0 && (
                    <svg
                      className={`w-3 h-3 text-gray-700 transition-transform ${
                        expandedSubcategories[subcategory.name]
                          ? "transform rotate-90"
                          : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  )}
                </div>

                {/* third level - sub-items */}
                {expandedSubcategories[subcategory.name] && subcategory.subItems && (
                  <div className="bg-gray-100">
                    {(() => {
                      // Group sub-items by section_title_l3 with proper typing
                      const apiCategory = apiFacilities.find(cat => cat.name === category.name);
                      const apiSub = apiCategory?.subcategories.find(sub => sub.name === subcategory.name);
                      
                      const groupedSubItems = subcategory.subItems.reduce((groups: Record<string, typeof subcategory.subItems>, subItem) => {
                        const apiSubItem = apiSub?.subItems?.find(item => item.name === subItem.name);
                        const sectionTitle = apiSubItem?.section_title_l3 || "no-section";
                        
                        if (!groups[sectionTitle]) {
                          groups[sectionTitle] = [];
                        }
                        groups[sectionTitle].push(subItem);
                        return groups;
                      }, {} as Record<string, typeof subcategory.subItems>);

                      return Object.entries(groupedSubItems).map(([sectionTitle, subItems], groupIndex) => (
                        <div key={groupIndex}>
                          {/* Section Title Header for third level */}
                          {sectionTitle && sectionTitle.trim() && sectionTitle !== "null" && sectionTitle !== "no-section" && (
                            <div className="bg-gray-300 cursor-default p-2 pl-10 border-t border-gray-200">
                              <div className="font-bold text-xs text-gray-800">
                                {sectionTitle}
                              </div>
                            </div>
                          )}
                          
                          {/* Sub-items under this section */}
                          {subItems.map((subItem, subItemIndex) => (
                            <div
                              key={subItemIndex}
                              className="p-2 pl-10 border-t border-gray-200 bg-white hover:bg-gray-200 flex items-center cursor-pointer"
                              onClick={() => {
                                if (handleSpecialFacilities(subItem.name)) {
                                  return;
                                }
                                const apiCategory = apiFacilities.find(cat => cat.name === category.name);
                                const apiSub = apiCategory?.subcategories.find(sub => sub.name === subcategory.name);
                                const apiSubItem = apiSub?.subItems?.find(item => item.name === subItem.name);
                                
                                const queryIdValue = apiSubItem?.query_id || subItem.name;
                                const facilityFloor = getFacilityFloor(queryIdValue) || getFacilityFloor(mapCategoryToFacility(subItem.name));
                              
                                if (facilityFloor) {
                                  setSpecialMapUrl(null); 
                                  const params = new URLSearchParams();
                                  params.append('facility', encodeURIComponent(queryIdValue));
                                  params.append('from', getKioskConfig().homePath);
                                  if (isDisplayToggled) params.append('display', 'toggled');
                                  
                                  let floorRoute = "";
                                  switch (facilityFloor) {
                                    case "1/F": floorRoute = "first-floor"; break;
                                    case "M/F": floorRoute = "m-floor"; break; 
                                    case "3/F": floorRoute = "third-floor"; break;
                                    case "2/F": floorRoute = "second-floor"; break;
                                    default:    floorRoute = "m-floor";
                                  }
                                  navigate(`/${floorRoute}?${params.toString()}`);
                                } else {
                                  setSpecialMapUrl(null);
                                  setFacility(queryIdValue);
                                  setQueryId(queryIdValue);
                                  console.log("API Sub-item selected:", subItem.name);
                                  console.log("Query ID:", queryIdValue);
                                }
                              }}
                            >
                              <div>
                                <div className="font-medium text-xs">
                                  {subItem.name}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ));
                    })()}
                  </div>
                )}
              </div>
            ))}
          </div>
        ));
      })()}
    </div>
  )}
              </div>
            ))}
          </div>
        </div>
        {/* Toggle tab - always visible */}
<div
  className={`fixed ${isPanelCollapsed ? "right-0" : "right-[25%]"} top-[40%] transform -translate-y-1/2 bg-teal-600 text-white p-2 cursor-pointer shadow-lg rounded-l-lg z-30 hover:bg-teal-700 transition-all duration-300 ease-in-out`}
  onClick={() => setIsPanelCollapsed(!isPanelCollapsed)}
>
  <svg className={`w-6 h-6 transition-transform duration-300 ${isPanelCollapsed ? "transform rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
</div>
      </div>
  
      {/* Legend Section - ALWAYS PRESENT (not part of toggle) */}
      <div className="w-full bg-white px-4 py-4">
        <div className="flex items-center justify-center gap-28 text-sm">
          {/* Available */}
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-300"></div>
            <span className="text-gray-700">Available 可用 / Idle 閒置</span>
          </div>
          
          {/* In Use */}
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-300"></div>
            <span className="text-gray-700">In Use 使用中 / Away 暫時離座</span>
          </div>
          
          {/* Maybe Available */}
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
            <span className="text-gray-700">Maybe Available / Away 暫時離座</span>
          </div>
        </div>
      </div>
  
      {/* CHANGED: Zone Info and Library Guidelines now appear here only when NOT toggled */}
      {!isDisplayToggled && (
        <>
          {/* Zone Info - New Averaged Display when not toggled */}
          <div className="bg-white p-4 mt-4 ">
            
            {loading && (
              <div className="text-center py-4">
                <div className="text-gray-500">Loading zone data...</div>
              </div>
            )}
            
            {error && (
              <div className="text-center py-4">
                <div className="text-red-500">Error: {error}</div>
              </div>
            )}
            
            {!loading && !error && (() => {
              const floorData = getFloorAverages(selectedLevel);
              
              if (!floorData) {
                return (
                  <div className="text-center py-8">
                    <div className="text-gray-500">No zone data available for {selectedLevel}</div>
                  </div>
                );
              }
              
              return (
                <div className="relative bg-gradient-to-r from-cyan-100 to-cyan-100 mx-16 rounded-2xl p-6 overflow-hidden">
                  {/* Curved cutout background */}
                  <div className="absolute bottom-0 left-0 w-28 h-24 bg-white rounded-tr-full"></div>
                  
                  {/* People icon positioned in the cutout */}
                  <div className="absolute bottom-5 left-3 z-10 scale-[1.8]">
                    {renderOccupancyIcon(floorData.occupancy)}
                  </div>
                  
                  {/* Metrics - moved to give space for icon */}
                  <div className="flex items-center justify-center gap-32 ml-20">
                    {/* Temperature */}
                    <div className="text-center relative">
                      <FaTemperatureHigh className="text-amber-500 size-6 text-lg absolute -top-1 -left-7" />
                      <div className="text-4xl font-semibold text-teal-700 mb-1">{floorData.avgTemp}°C</div>
                      <div className="text-sm text-teal-600">Temperature</div>
                    </div>
                    
                    {/* Humidity */}
                    <div className="text-center relative">
                      <FaTint className="text-blue-400 size-7 text-lg absolute -top-1 -left-4" />
                      <div className="text-4xl font-semibold text-teal-700 mb-1">{floorData.avgHumidity}%</div>
                      <div className="text-sm text-teal-600">Relative Humidity</div>
                    </div>
                    
                    {/* CO2 */}
                    <div className="text-center relative">
                      <div className="scale-75 absolute -top-2 -left-6">
                        {renderCO2StatusIcon(floorData.avgCO2)}
                      </div>
                      <div className="text-4xl font-semibold text-teal-700 mb-1">
                        {floorData.avgCO2} <span className="text-sm text-gray-500">ppm</span>
                      </div>
                      <div className="text-sm text-teal-600">Carbon Dioxide (CO2)</div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
  
          {/* Library Guidelines Section when not toggled */}
          <div className="bg-white p-2 mt-8">
            <div className="flex items-center gap-12 w-full justify-center mx-8">
              {/* Left side - Triangular icon formation */}
              <div className="flex-shrink-0 ml-3">
                <div className="relative w-24 h-20">
                  {/* Top icon */}
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center">
                    <Laptop className="w-7 h-7 text-blue-600" />
                  </div>
                  
                  {/* Bottom left icon */}
                  <div className="absolute -bottom-6 -left-4 w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
                    <Briefcase className="w-7 h-7 text-green-600" />
                  </div>
                  
                  {/* Bottom right icon */}
                  <div className="absolute -bottom-6 -right-4 w-14 h-14 bg-yellow-100 rounded-full flex items-center justify-center">
                    <Lightbulb className="w-7 h-7 text-yellow-600" />
                  </div>
                </div>
              </div>
  
              {/* Right side - Text content */}
              <div className="flex-1 space-y-4">
                {/* First message */}
                <div>
                  <p className="text-gray-800 text-[16px] leading-relaxed">
                    For unattended items found in the Library, please help relocate them to the nearby unattended items collection box.
                  </p>
                  <p className="text-gray-700 text-[18px] mt-1">
                    如發現圖書館內有無人看管的物品，請移至附近的閒置物品收集點。
                  </p>
                </div>
                
                {/* Second message */}
                <div>
                  <p className="text-gray-800 text-[16px] leading-relaxed">
                    Always be considerate and take your belongings with you ALL the time to vacate the study space.
                  </p>
                  <p className="text-gray-700 text-[18px] mt-1">
                    文明使用圖書館，共享學習空間。離開時，請緊記帶走個人物品。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
  
  <button
        onClick={() => setIsDisplayToggled(!isDisplayToggled)}
        className={`fixed ${
          isDisplayToggled ? "bottom-[530px]" : "bottom-[530px]"
        } right-6 bg-blue-600 hover:bg-blue-700 text-white py-8 px-14 rounded-full shadow-lg transition-all duration-200 z-50 flex items-center justify-center`}
        title="Adjust Display"
      >
       <img src="chair.svg" className=" w-14 h-14 mr-4" />
        <span className="text-4xl font-semibold">Adjust Display</span>
      </button>

  {/* Image Slider */}
{!sliderApiLoading && sliderApiData.length > 0 && (
  <div className="absolute bottom-2 mx-28 z-40">
    <div className="p-4 max-w-4xl">
      <div className="relative">
        <div className="flex items-center gap-4">
          {/* Main Image */}
          <img
            src={sliderApiData[currentSlide]?.image}
            alt={`Slide ${currentSlide + 1}`}
            className="flex-1 h-full object-cover rounded-md"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          
          {/* QR Image - only show if it exists */}
          {sliderApiData[currentSlide]?.qrimage && (
            <img
              src={sliderApiData[currentSlide]?.qrimage}
              alt={`QR Code ${currentSlide + 1}`}
              className="w-96 h-96 object-contain flex-shrink-0"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
        </div>
        
        {/* Text below images */}
        <div
          className="text-xl text-center text-gray-900 leading-7 mt-3"
          dangerouslySetInnerHTML={{ 
            __html: sliderApiData[currentSlide]?.text || '' 
          }}
        />
      </div>
    </div>
  </div>
)}
      
    </div>
  );
};

export default MFZoomViewer;